/**
 * App identity for the backend's caller allow-list.
 *
 * The Ringg backend allow-lists an agent's callers by `Origin`. A browser
 * attaches it automatically; a native app sends nothing, so it has to present
 * its own identity — by convention `<platform>://<bundleId>`, the scheme the
 * backend accepts for app clients. Without it the webcall request is rejected
 * before authentication even matters (`400 Origin header is required`).
 *
 * The bundle id is the INTEGRATOR's, not ours, so the value allow-listed in
 * the dashboard is their app. React Native cannot read it without a native
 * module, and this package refuses to grow one for a single string — so the id
 * is a parameter. Expo apps have it in `expo-application`'s `applicationId`;
 * bare apps already know their own.
 *
 * Mirrors `flutter/lib/src/platform/app_origin.dart`, which derives the same
 * string from `package_info_plus`.
 */

import { Platform } from "react-native";

/** Schemes the backend accepts, keyed by `Platform.OS`. */
const SCHEMES: Partial<Record<typeof Platform.OS, string>> = {
  android: "android",
  ios: "ios",
  macos: "macos",
  windows: "windows",
};

/**
 * `<platform>://<bundleId>` for the running app — e.g. `android://com.acme.app`.
 *
 * Returns undefined on a platform the backend has no scheme for, which
 * includes react-native-web: there the browser sends a real `Origin` and
 * setting one from JavaScript is forbidden anyway.
 */
export const appOrigin = (bundleId: string): string | undefined => {
  const scheme = SCHEMES[Platform.OS];
  if (!scheme || !bundleId) return undefined;
  return `${scheme}://${bundleId}`;
};
